import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import {
  PackageIcon,
  TruckIcon,
  CheckCircleIcon,
  SealCheckIcon,
  CoinsIcon,
  StarIcon,
} from "@phosphor-icons/react";
import OrderProgress from "@/components/OrderProgress";

const FALLBACK_CARRIERS = [
  { slug: "usps", name: "USPS" },
  { slug: "ups", name: "UPS" },
  { slug: "fedex", name: "FedEx" },
  { slug: "dhl", name: "DHL" },
  { slug: "other", name: "Other" },
];

export default function OrderDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tracking, setTracking] = useState("");
  const [carrier, setCarrier] = useState("usps");
  const [carriers, setCarriers] = useState(FALLBACK_CARRIERS);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/orders/${id}`);
      setOrder(data);
    } catch (e) {
      toast.error(apiError(e));
      navigate("/orders");
    } finally {
      setLoading(false);
    }
  };

  // Load the carrier registry + seller's offered carriers (when the
  // current viewer is the seller). The seller's offered set narrows the
  // dropdown so they only see what they actually ship with.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const reg = await api.get("/shipping/carriers");
        const all = reg.data?.items || [];
        let offered = all;
        if (user) {
          try {
            const prof = await api.get("/me/shipping-profile");
            const allowed = prof.data?.carriers || [];
            if (allowed.length) {
              offered = all.filter((c) => allowed.includes(c.slug));
              if (offered.length === 0) offered = all;
            }
          } catch (_) {
            /* not a seller / no profile yet — keep all carriers */
          }
        }
        if (alive) {
          setCarriers(offered);
          if (offered.length && !offered.find((c) => c.slug === carrier)) {
            setCarrier(offered[0].slug);
          }
        }
      } catch (_) {
        /* keep fallback */
      }
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    if (!user) {
      navigate("/login");
      return;
    }
    load();
    // eslint-disable-next-line
  }, [id, user]);

  const ship = async () => {
    // Sellers must supply tracking; admins are allowed to override and
    // mark shipped without one (manual hand-off / lost-mail recovery).
    if (!tracking && !iAmAdmin) return toast.error("Tracking number required");
    setBusy(true);
    try {
      await api.post(`/orders/${id}/ship`, { tracking_number: tracking || null, carrier });
      toast.success("Marked as shipped");
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const deliver = async () => {
    setBusy(true);
    try {
      await api.post(`/orders/${id}/deliver`);
      toast.success("Delivery confirmed. Payout released to seller.");
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (!user) return null;
  if (loading)
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="max-w-4xl mx-auto p-10 text-zinc-500">Loading…</div>
      </div>
    );
  if (!order) return null;

  const iAmBuyer = user.user_id === order.buyer_id;
  const iAmSeller = user.user_id === order.seller_id;
  const iAmAdmin = user.role === "admin";

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid={`order-detail-${order.id}`}>
      <Navbar />
      <main className="flex-1 max-w-4xl mx-auto w-full px-6 py-10">
        <Link to="/orders" className="text-sm text-zinc-500 hover:text-[#1D78AB]">
          ← Orders
        </Link>
        <p className="overline text-zinc-500 mt-4">ORDER #{order.id.slice(-8).toUpperCase()}</p>
        <h1 className="font-display font-black text-3xl lg:text-4xl tracking-tighter mt-1">
          {order.listing_title}
        </h1>

        <div className="mt-8 grid md:grid-cols-3 gap-6">
          <div className="md:col-span-2 space-y-6">
            <OrderProgress order={order} variant="detail" />

            {!order.is_digital && (
              <ShippingAddressPanel order={order} viewerIsBuyer={iAmBuyer} viewerIsSeller={iAmSeller} onUpdated={load} />
            )}

            {/* For digital orders the buyer downloaded the file from their
                Orders → Downloads tab the moment the order was paid —
                there's nothing to ship and no shipping address was even
                collected at checkout. Show a quick informational panel
                to the seller instead of the ship-tracking form. */}
            {iAmSeller && order.is_digital && (
              <div className="border border-emerald-300 bg-emerald-50 p-6 rounded-xl" data-testid="digital-no-ship-panel">
                <div className="flex items-center gap-2 mb-1">
                  <CheckCircleIcon size={22} weight="fill" className="text-emerald-700" />
                  <div className="font-display font-bold text-lg text-emerald-900">Delivered — digital download</div>
                </div>
                <p className="text-sm text-emerald-800">
                  This is a digital product. The buyer received instant access
                  to the file in their Orders → Downloads tab when the
                  payment cleared. No shipping required.
                </p>

                {/* Read-receipt — visible only to the seller. Shows first
                    open timestamp (the "read receipt"), most recent open,
                    and total download count. If the buyer hasn't opened
                    the file yet we show a muted "Not opened yet" state. */}
                <div
                  className="mt-4 border-t border-emerald-200 pt-4 grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs"
                  data-testid="digital-read-receipt"
                >
                  <div>
                    <div className="overline text-emerald-700 mb-0.5">First opened</div>
                    <div
                      className={`font-bold ${order.first_downloaded_at ? "text-emerald-900" : "text-zinc-500"}`}
                      data-testid="receipt-first-opened"
                    >
                      {order.first_downloaded_at
                        ? new Date(order.first_downloaded_at).toLocaleString()
                        : "Not opened yet"}
                    </div>
                  </div>
                  <div>
                    <div className="overline text-emerald-700 mb-0.5">Last opened</div>
                    <div
                      className={`font-bold ${order.last_downloaded_at ? "text-emerald-900" : "text-zinc-500"}`}
                      data-testid="receipt-last-opened"
                    >
                      {order.last_downloaded_at
                        ? new Date(order.last_downloaded_at).toLocaleString()
                        : "—"}
                    </div>
                  </div>
                  <div>
                    <div className="overline text-emerald-700 mb-0.5">Downloads</div>
                    <div
                      className="font-bold text-emerald-900"
                      data-testid="receipt-download-count"
                    >
                      {order.download_count || 0}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {(iAmSeller || iAmAdmin) && !order.is_digital && order.shipping_status === "pending" && (iAmAdmin || order.shipping_address) && (
              <div className="border border-zinc-200 bg-white p-6" data-testid={iAmAdmin && !iAmSeller ? "admin-ship-panel" : "seller-ship-panel"}>
                <div className="flex items-center gap-2 mb-3">
                  <TruckIcon size={22} className="text-[#1D78AB]" />
                  <div className="font-display font-bold text-lg">
                    {iAmAdmin && !iAmSeller ? "Admin: Mark shipped" : "Ship this order"}
                  </div>
                  {iAmAdmin && !iAmSeller && (
                    <span className="ml-2 inline-flex items-center rounded-full bg-amber-100 text-amber-800 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5">
                      Admin override
                    </span>
                  )}
                </div>
                <p className="text-sm text-zinc-600 mb-4">
                  {iAmAdmin && !iAmSeller
                    ? "Override: mark this order shipped on the seller's behalf. The buyer is notified and payout follows the normal release rules."
                    : "Enter the tracking number to notify the buyer. Your payout releases after the buyer confirms delivery."}
                </p>
                <div className="grid sm:grid-cols-[1fr_160px_auto] gap-2">
                  <div>
                    <Label className="overline">
                      Tracking number {iAmAdmin && !iAmSeller && <span className="text-zinc-400 normal-case font-medium">(optional)</span>}
                    </Label>
                    <Input
                      value={tracking}
                      onChange={(e) => setTracking(e.target.value)}
                      placeholder={iAmAdmin && !iAmSeller ? "Optional — leave blank to override" : "1Z999AA10123456784"}
                      className="mt-1"
                      data-testid="tracking-input"
                    />
                  </div>
                  <div>
                    <Label className="overline">Carrier</Label>
                    <select
                      value={carrier}
                      onChange={(e) => setCarrier(e.target.value)}
                      className="w-full mt-1 h-10 border border-zinc-300 bg-white px-3 text-sm"
                      data-testid="carrier-select"
                    >
                      {carriers.map((c) => (
                        <option key={c.slug} value={c.slug}>{c.name}</option>
                      ))}
                    </select>
                  </div>
                  <Button
                    onClick={ship}
                    disabled={busy}
                    className="self-end bg-[#1D78AB] hover:bg-[#165d85] text-white"
                    data-testid="mark-shipped-btn"
                  >
                    Mark shipped
                  </Button>
                </div>
              </div>
            )}

            {iAmBuyer && order.shipping_status === "shipped" && (
              <div className="border border-zinc-200 bg-white p-6">
                <div className="flex items-center gap-2 mb-2">
                  <PackageIcon size={22} className="text-[#1D78AB]" />
                  <div className="font-display font-bold text-lg">Arrived?</div>
                </div>
                <p className="text-sm text-zinc-600 mb-2">
                  Your order shipped via <b>{order.carrier}</b> · tracking{" "}
                  <span className="font-mono">{order.tracking_number}</span>.
                </p>
                {order.tracking_url && (
                  <a
                    href={order.tracking_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#1D78AB] hover:underline mb-4"
                    data-testid="track-shipment-link"
                  >
                    <TruckIcon size={14} weight="fill" />
                    Track shipment on {order.carrier} →
                  </a>
                )}
                <p className="text-sm text-zinc-600 mb-4 mt-2">
                  Once you've received it, confirm delivery to release the seller's payout.
                </p>
                <Button
                  onClick={deliver}
                  disabled={busy}
                  className="bg-[#1D78AB] hover:bg-[#165d85] text-white"
                  data-testid="confirm-delivery-btn"
                >
                  Confirm delivery
                </Button>
              </div>
            )}

            {order.shipping_status === "shipped" && !iAmBuyer && (
              <div className="border border-zinc-200 bg-white p-6">
                <div className="font-display font-bold text-lg">Shipped</div>
                <p className="text-sm text-zinc-600 mt-1">
                  {order.carrier} · tracking{" "}
                  <span className="font-mono">{order.tracking_number}</span>
                </p>
                {order.tracking_url && (
                  <a
                    href={order.tracking_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#1D78AB] hover:underline mt-2"
                    data-testid="track-shipment-link-seller"
                  >
                    Open carrier tracking →
                  </a>
                )}
                <p className="text-xs text-zinc-500 mt-2">
                  Waiting for buyer to confirm delivery. Payout on hold.
                </p>
                {iAmAdmin && (
                  <div className="mt-4 pt-4 border-t border-zinc-200">
                    <p className="text-[11px] uppercase tracking-wider font-bold text-amber-700 mb-2">
                      Admin override
                    </p>
                    <p className="text-xs text-zinc-600 mb-3">
                      Force-mark this order as delivered. Releases the seller's
                      held payout and stops the "awaiting confirmation" loop —
                      use when the buyer is unresponsive or the carrier has
                      confirmed delivery out-of-band.
                    </p>
                    <Button
                      onClick={async () => {
                        if (!window.confirm("Mark this order as delivered? This releases the seller's payout immediately.")) return;
                        try {
                          await api.post(`/orders/${id}/deliver`);
                          toast.success("Marked as delivered", {
                            description: "Payout released to the seller.",
                          });
                          load();
                        } catch (e) {
                          toast.error(apiError(e) || "Could not mark as delivered");
                        }
                      }}
                      className="rounded-full bg-amber-500 hover:bg-amber-600 text-white font-bold"
                      data-testid="admin-mark-delivered-btn"
                    >
                      <CheckCircleIcon size={14} weight="fill" className="mr-2" />
                      Mark as delivered (admin)
                    </Button>
                  </div>
                )}
              </div>
            )}

            {order.shipping_status === "delivered" && (
              <div className="border border-emerald-300 bg-emerald-50 p-6 flex items-center gap-3">
                <CheckCircleIcon size={24} weight="fill" className="text-emerald-600" />
                <div>
                  <div className="font-display font-bold">Delivered</div>
                  <p className="text-sm text-zinc-700">
                    {order.delivered_at
                      ? new Date(order.delivered_at).toLocaleString()
                      : ""}
                  </p>
                </div>
              </div>
            )}

            {iAmBuyer && ["paid", "shipped", "delivered"].includes(order.status) && (
              <ReviewForm order={order} />
            )}
          </div>

          <aside className="border border-zinc-200 p-5 space-y-4 h-fit bg-white">
            <div>
              <p className="overline text-zinc-500">TOTAL</p>
              <div className="font-display font-black text-3xl text-[#1D78AB] font-mono">
                ${Number(order.amount).toFixed(2)}
              </div>
              {Number(order.credits_applied_cents || 0) > 0 && (
                <div className="mt-2 text-xs" data-testid="order-credits-applied">
                  <span className="text-emerald-600 font-semibold">
                    −${(order.credits_applied_cents / 100).toFixed(2)} Plus credit
                  </span>{" "}
                  <span className="text-zinc-500">
                    (item ${Number(order.gross_amount || order.amount).toFixed(2)})
                  </span>
                </div>
              )}
            </div>
            <div>
              <p className="overline text-zinc-500">PAID WITH</p>
              <p className="text-sm capitalize">{order.provider || "stripe"}</p>
            </div>
            <div>
              <p className="overline text-zinc-500">SEGMENT</p>
              <span
                className={`inline-block px-2 py-0.5 text-[10px] font-bold tracking-widest uppercase ${
                  order.segment === "wholesale" ? "bg-amber-500 text-white" : "bg-[#1D78AB] text-white"
                }`}
              >
                {order.segment}
              </span>
            </div>
            {iAmSeller && order.payout && (
              <div className="border-t border-zinc-200 pt-4">
                <div className="flex items-center gap-2 mb-1">
                  <CoinsIcon size={16} className="text-[#1D78AB]" />
                  <p className="overline text-zinc-500">YOUR PAYOUT (NET)</p>
                </div>
                <div className="font-mono font-bold text-2xl text-emerald-600">
                  ${Number(order.payout.net_amount ?? order.payout.amount).toFixed(2)}
                </div>
                {Number(order.platform_fee || 0) > 0 && (
                  <div className="mt-3 border border-zinc-100 rounded-lg p-3 bg-zinc-50 space-y-1 text-xs">
                    <div className="flex justify-between text-zinc-600">
                      <span>Gross sale</span>
                      <span className="font-mono">${Number(order.amount).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-zinc-500">
                      <span>Platform fee (5% + 2.5% + $0.25)</span>
                      <span className="font-mono">
                        −${Number(order.platform_fee).toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between pt-1 border-t border-zinc-200 font-bold text-zinc-900">
                      <span>Net payout</span>
                      <span className="font-mono">
                        ${Number(order.seller_net ?? order.payout.net_amount ?? order.payout.amount).toFixed(2)}
                      </span>
                    </div>
                  </div>
                )}
                <p
                  className={`text-xs mt-3 font-semibold ${
                    order.payout.status === "released" ? "text-emerald-600" : "text-amber-600"
                  }`}
                >
                  {order.payout.status === "released" ? (
                    <>
                      <SealCheckIcon size={12} className="inline mr-1" /> Released
                    </>
                  ) : (
                    "Held — releases on delivery"
                  )}
                </p>
              </div>
            )}
          </aside>
        </div>
      </main>
      <Footer />
    </div>
  );
}

function Timeline({ order }) {
  const steps = [
    { key: "paid", label: "Payment received", at: order.created_at },
    { key: "shipped", label: "Shipped by seller", at: order.shipped_at },
    { key: "delivered", label: "Delivered to buyer", at: order.delivered_at },
  ];
  const reached = (k) => {
    if (k === "paid") return true;
    if (k === "shipped") return ["shipped", "delivered"].includes(order.shipping_status);
    if (k === "delivered") return order.shipping_status === "delivered";
    return false;
  };
  return (
    <div className="border border-zinc-200 bg-white p-6">
      <p className="overline text-zinc-500 mb-4">TIMELINE</p>
      <div className="space-y-5">
        {steps.map((s, i) => (
          <div key={s.key} className="flex items-start gap-3">
            <div
              className={`h-6 w-6 rounded-full grid place-items-center flex-shrink-0 ${
                reached(s.key) ? "bg-[#1D78AB] text-white" : "bg-zinc-200 text-zinc-500"
              }`}
            >
              {reached(s.key) ? <CheckCircleIcon size={14} weight="fill" /> : <span className="text-xs">{i + 1}</span>}
            </div>
            <div className="flex-1 -mt-0.5">
              <div className={`font-semibold text-sm ${reached(s.key) ? "" : "text-zinc-400"}`}>
                {s.label}
              </div>
              {s.at && (
                <div className="text-xs text-zinc-500">{new Date(s.at).toLocaleString()}</div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ReviewForm({ order }) {
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/sellers/${order.seller_id}/reviews`, {
        rating,
        comment,
        order_id: order.id,
      });
      setSubmitted(true);
      toast.success("Thanks — review posted!");
    } catch (e) {
      toast.error(apiError(e));
    }
    setBusy(false);
  };

  if (submitted) {
    return (
      <div
        className="border border-emerald-200 bg-emerald-50 p-6 rounded-xl text-emerald-800"
        data-testid="review-submitted"
      >
        <CheckCircleIcon size={20} weight="fill" className="inline mr-2" />
        Review posted. Thank you!
      </div>
    );
  }

  return (
    <div
      className="border border-zinc-200 p-6 rounded-xl bg-white"
      data-testid="review-form"
    >
      <div className="flex items-center gap-2 mb-3">
        <StarIcon size={22} weight="fill" className="text-amber-500" />
        <p className="font-display font-bold text-lg">How was the seller?</p>
      </div>
      <p className="text-sm text-zinc-600 mb-4">
        Your review helps other buyers trust Bidorah sellers.
      </p>
      <div className="flex gap-1 mb-3" data-testid="review-stars">
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            onClick={() => setRating(n)}
            aria-label={`Rate ${n} stars`}
            data-testid={`review-star-${n}`}
          >
            <StarIcon
              size={28}
              weight={n <= rating ? "fill" : "regular"}
              className={n <= rating ? "text-amber-500" : "text-zinc-300"}
            />
          </button>
        ))}
      </div>
      <textarea
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        placeholder="What stood out? (optional)"
        maxLength={1000}
        rows={3}
        className="w-full border border-zinc-300 rounded-lg p-3 text-sm"
        data-testid="review-comment"
      />
      <div className="mt-3 flex justify-end">
        <Button
          onClick={submit}
          disabled={busy}
          className="rounded-full"
          data-testid="review-submit"
        >
          {busy ? "Posting…" : "Post review"}
        </Button>
      </div>
    </div>
  );
}



/**
 * ShippingAddressPanel — collects the buyer's shipping address after
 * checkout and shows it to the seller before they ship. Lives directly
 * above the seller's "Ship this order" block so the seller can copy
 * the address into their label-printer of choice.
 *
 * UX rules:
 *   • Buyers can EDIT their address as long as the order is still
 *     `pending` (server enforces this too).
 *   • Sellers see a read-only display.
 *   • If no address yet, buyers see a clearly-flagged form.
 */
function ShippingAddressPanel({ order, viewerIsBuyer, viewerIsSeller, onUpdated }) {
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState(() => ({
    full_name: "",
    line1: "",
    line2: "",
    city: "",
    state: "",
    postal_code: "",
    country: "US",
    phone: "",
    ...(order.shipping_address || {}),
  }));

  // When the buyer has no saved address on the order, fetch their default
  // (set last time they shipped to themselves) as a one-tap autofill.
  useEffect(() => {
    if (order.shipping_address || !viewerIsBuyer) return;
    (async () => {
      try {
        const r = await api.get("/me/default-shipping-address");
        const a = r.data?.shipping_address;
        if (a) setForm((f) => ({ ...f, ...a }));
      } catch (_) {
        /* ignore */
      }
    })();
  }, [order.id, viewerIsBuyer, order.shipping_address]);

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/orders/${order.id}/shipping-address`, form);
      toast.success("Shipping address saved");
      setEditing(false);
      onUpdated?.();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const addr = order.shipping_address;
  const canEdit = viewerIsBuyer && order.shipping_status === "pending";

  // Read-only state — address exists and not in edit mode.
  if (addr && !editing) {
    return (
      <div className="border border-zinc-200 bg-white p-6" data-testid="shipping-address-panel">
        <div className="flex items-center justify-between gap-3 mb-2">
          <div className="font-display font-bold text-base">
            {viewerIsSeller ? "Ship to" : "Your shipping address"}
          </div>
          {canEdit && (
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="text-xs font-semibold text-[#1D78AB] hover:underline"
              data-testid="edit-shipping-address-btn"
            >
              Edit
            </button>
          )}
        </div>
        <div className="text-sm text-zinc-700 leading-relaxed">
          <div className="font-semibold">{addr.full_name}</div>
          <div>{addr.line1}</div>
          {addr.line2 && <div>{addr.line2}</div>}
          <div>
            {addr.city}, {addr.state} {addr.postal_code}
          </div>
          <div className="text-zinc-500">{addr.country}</div>
          {addr.phone && <div className="text-zinc-500">{addr.phone}</div>}
        </div>
      </div>
    );
  }

  // No address yet — only buyer can fill it in.
  if (!addr && !viewerIsBuyer) {
    return (
      <div className="border border-amber-200 bg-amber-50 p-5 text-sm text-amber-900" data-testid="shipping-address-pending">
        Waiting on the buyer to confirm their shipping address. You'll be able to ship as soon as
        they do.
      </div>
    );
  }

  if (!viewerIsBuyer) return null;

  // Editable form (either no address yet or buyer hit Edit).
  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  return (
    <div className="border border-zinc-200 bg-white p-6" data-testid="shipping-address-form">
      <div className="font-display font-bold text-lg mb-1">
        {addr ? "Edit shipping address" : "Where should we ship this?"}
      </div>
      <p className="text-sm text-zinc-600 mb-4">
        {addr
          ? "You can update this anytime before the seller ships your order."
          : "The seller can't ship until you confirm your shipping address."}
      </p>
      <div className="grid sm:grid-cols-2 gap-3">
        <div className="sm:col-span-2">
          <Label className="overline">Full name</Label>
          <Input value={form.full_name} onChange={onChange("full_name")} className="mt-1" data-testid="addr-full-name" />
        </div>
        <div className="sm:col-span-2">
          <Label className="overline">Address line 1</Label>
          <Input value={form.line1} onChange={onChange("line1")} className="mt-1" data-testid="addr-line1" />
        </div>
        <div className="sm:col-span-2">
          <Label className="overline">Address line 2 <span className="text-zinc-400 normal-case">— optional</span></Label>
          <Input value={form.line2 || ""} onChange={onChange("line2")} className="mt-1" data-testid="addr-line2" />
        </div>
        <div>
          <Label className="overline">City</Label>
          <Input value={form.city} onChange={onChange("city")} className="mt-1" data-testid="addr-city" />
        </div>
        <div className="grid grid-cols-[1fr_1.2fr] gap-2">
          <div>
            <Label className="overline">State</Label>
            <Input value={form.state} onChange={onChange("state")} className="mt-1" maxLength={2} data-testid="addr-state" />
          </div>
          <div>
            <Label className="overline">ZIP</Label>
            <Input value={form.postal_code} onChange={onChange("postal_code")} className="mt-1" data-testid="addr-zip" />
          </div>
        </div>
        <div>
          <Label className="overline">Country</Label>
          <Input value={form.country} onChange={onChange("country")} className="mt-1" maxLength={2} data-testid="addr-country" />
        </div>
        <div>
          <Label className="overline">Phone <span className="text-zinc-400 normal-case">— optional</span></Label>
          <Input value={form.phone || ""} onChange={onChange("phone")} className="mt-1" data-testid="addr-phone" />
        </div>
      </div>
      <div className="mt-4 flex items-center gap-3 justify-end">
        {addr && (
          <button
            type="button"
            onClick={() => setEditing(false)}
            className="text-sm text-zinc-600 hover:underline"
            data-testid="addr-cancel-btn"
          >
            Cancel
          </button>
        )}
        <Button
          onClick={save}
          disabled={busy}
          className="bg-[#1D78AB] hover:bg-[#165d85] text-white rounded-full"
          data-testid="addr-save-btn"
        >
          {busy ? "Saving…" : "Save shipping address"}
        </Button>
      </div>
    </div>
  );
}
